import { useEditorStore } from "@/store";
import { LAYER_TYPE_COLORS, LAYER_TYPE_ICONS, type LayerType } from "@/styles/theme";
import { ColorField } from "@/components/fields/ColorField";
import { NumberField } from "@/components/fields/NumberField";
import { StringField } from "@/components/fields/StringField";
import { SelectField } from "@/components/fields/SelectField";
import { BooleanField } from "@/components/fields/BooleanField";
import { ArrayField } from "@/components/fields/ArrayField";
import { SpriteField } from "@/components/fields/SpriteField";
import { FontField } from "@/components/fields/FontField";
import { PropertyGroup } from "@/components/fields/PropertyGroup";
import { useSourceLayers } from "@/hooks/useSourceLayers";
import { FilterEditor } from "@/components/editor/filters/FilterEditor";
import { useFeatureProperties } from "@/hooks/useFeatureProperties";
import { PROPERTY_DESCRIPTIONS, LAYER_TYPE_DESCRIPTIONS } from "@/lib/propertyDescriptions";
import { flyToZoom } from "@/lib/mapCommands";
import type { StoreLayer } from "@/store";

/** Shorthand to get tooltip for a property */
const tip = (key: string) => PROPERTY_DESCRIPTIONS[key];

interface LayerEditorProps {
  layer: StoreLayer;
  onRemove: () => void;
}

export function LayerEditor({ layer, onRemove }: LayerEditorProps) {
  const updateLayerPaint = useEditorStore((s) => s.updateLayerPaint);
  const updateLayerLayout = useEditorStore((s) => s.updateLayerLayout);
  const updateLayerCommon = useEditorStore((s) => s.updateLayerCommon);
  const setStyle = useEditorStore((s) => s.setStyle);
  const style = useEditorStore((s) => s.style);
  const originalStyle = useEditorStore((s) => s.originalStyle);
  const sources = useEditorStore((s) => s.style.sources ?? {});
  const currentZoom = useEditorStore((s) => s.viewport.zoom);

  // Check if this layer existed in the original style
  const originalLayer = originalStyle?.layers.find((l) => l.id === layer.id);

  const handleReset = () => {
    if (!originalLayer) return;
    // Replace this layer with the original version
    const newStyle = JSON.parse(JSON.stringify(style)) as typeof style;
    const idx = newStyle.layers.findIndex((l) => l.id === layer.id);
    if (idx >= 0) {
      newStyle.layers[idx] = JSON.parse(JSON.stringify(originalLayer)) as StoreLayer;
      setStyle(newStyle);
    }
  };
  const layerType = layer.type as LayerType;
  const paint = layer.paint;
  const layout = layer.layout;

  const { sourceLayers, loading: sourceLayersLoading } = useSourceLayers(layer.source);
  const featureProperties = useFeatureProperties(layer.source, layer["source-layer"]);

  const sourceObj = layer.source
    ? (sources as Record<string, Record<string, unknown>>)[layer.source]
    : undefined;
  const srcMinZoom = (sourceObj?.minzoom as number | undefined) ?? 0;
  const layerMinZoom = (layer.minzoom as number | undefined) ?? srcMinZoom;
  const layerMaxZoom = (layer.maxzoom as number | undefined) ?? 24;

  const p = (key: string) => paint?.[key];
  const l = (key: string) => layout?.[key];
  const sp = (key: string, val: unknown) => updateLayerPaint(layer.id, key, val);
  const sl = (key: string, val: unknown) => updateLayerLayout(layer.id, key, val);

  /** Check if any of the given keys have non-undefined values */
  const hasAny = (obj: Record<string, unknown> | undefined, ...keys: string[]) =>
    keys.some((k) => obj?.[k] !== undefined);

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <span
          className="flex h-6 w-6 items-center justify-center rounded-radius-sm text-xs text-white"
          style={{ backgroundColor: LAYER_TYPE_COLORS[layerType] ?? "var(--color-border-muted)" }}
        >
          {LAYER_TYPE_ICONS[layerType] ?? "?"}
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-text-primary">{layer.id}</div>
          <div className="text-[10px] text-text-muted">{LAYER_TYPE_DESCRIPTIONS[layer.type] ?? layer.type}</div>
        </div>
        <div className="flex gap-1.5">
          {originalLayer && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 rounded-radius-sm border border-border-muted px-2.5 py-1 text-[11px] font-medium text-text-secondary hover:bg-surface-sunken transition-colors"
              title="Reset this layer to its original state"
            >
              ↺ Reset
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="flex items-center gap-1 rounded-radius-sm border border-error/30 bg-error-muted px-2.5 py-1 text-[11px] font-medium text-error hover:bg-error hover:text-white transition-colors"
          >
            ✕ Remove
          </button>
        </div>
      </div>

      {/* Zoom visibility hint */}
      {(() => {
        const minZ = layer.minzoom ?? 0;
        const maxZ = layer.maxzoom ?? 24;
        const isHidden = layer.layout?.visibility === "none";
        const midZoom = (minZ + maxZ) / 2;

        if (isHidden) {
          return (
            <div className="mb-3 flex items-center gap-2 rounded-radius-sm bg-warning/10 px-3 py-2">
              <span className="text-[11px] text-warning">○</span>
              <span className="flex-1 text-[11px] text-warning">Layer is hidden (visibility: none)</span>
            </div>
          );
        }
        if (currentZoom < minZ) {
          return (
            <div className="mb-3 flex items-center gap-2 rounded-radius-sm bg-warning/10 px-3 py-2">
              <span className="text-[11px] text-warning">▲</span>
              <span className="flex-1 text-[11px] text-warning">
                Not visible — zoom in past z{minZ} (current: z{currentZoom.toFixed(1)})
              </span>
              <button
                type="button"
                onClick={() => flyToZoom(midZoom)}
                className="shrink-0 rounded-radius-sm border border-warning/30 bg-warning/20 px-2 py-0.5 text-[10px] font-medium text-warning hover:bg-warning hover:text-white transition-colors"
              >
                Zoom to z{midZoom.toFixed(0)}
              </button>
            </div>
          );
        }
        if (currentZoom >= maxZ) {
          return (
            <div className="mb-3 flex items-center gap-2 rounded-radius-sm bg-warning/10 px-3 py-2">
              <span className="text-[11px] text-warning">▼</span>
              <span className="flex-1 text-[11px] text-warning">
                Not visible — zoom out below z{maxZ} (current: z{currentZoom.toFixed(1)})
              </span>
              <button
                type="button"
                onClick={() => flyToZoom(midZoom)}
                className="shrink-0 rounded-radius-sm border border-warning/30 bg-warning/20 px-2 py-0.5 text-[10px] font-medium text-warning hover:bg-warning hover:text-white transition-colors"
              >
                Zoom to z{midZoom.toFixed(0)}
              </button>
            </div>
          );
        }
        return null;
      })()}

      {/* Filter */}
      <PropertyGroup title="Filter" hasValues={layer.filter != null}>
        <FilterEditor
          value={layer.filter}
          onChange={(v) => updateLayerCommon(layer.id, "filter", v)}
          featureProperties={featureProperties}
        />
      </PropertyGroup>

      {/* ── Common ── */}
      <PropertyGroup title="Common" defaultOpen>
        {"source" in layer && (
          <SelectField
            label="Source"
            value={layer.source as string}
            onChange={(v) => updateLayerCommon(layer.id, "source", v)}
            options={Object.keys(sources).map((id) => ({ value: id, label: id }))}
            description={tip("source")}
          />
        )}
        {"source-layer" in layer && (
          sourceLayers.length > 0 ? (
            <SelectField
              label="Source Layer"
              value={layer["source-layer"] as string}
              onChange={(v) => updateLayerCommon(layer.id, "source-layer", v)}
              options={sourceLayers.map((sl_item) => ({
                value: sl_item.id,
                label: sl_item.description ? `${sl_item.id} — ${sl_item.description}` : sl_item.id,
              }))}
              description={sourceLayersLoading ? "Loading..." : `${sourceLayers.length} available`}
            />
          ) : (
            <StringField
              label="Source Layer"
              value={layer["source-layer"] as string}
              onChange={(v) => updateLayerCommon(layer.id, "source-layer", v)}
              description={sourceLayersLoading ? "Loading source layers..." : undefined}
            />
          )
        )}
        <NumberField
          label="Min Zoom"
          value={layer.minzoom}
          onChange={(v) => updateLayerCommon(layer.id, "minzoom", Math.max(srcMinZoom, Math.min(v, layerMaxZoom)))}
          min={srcMinZoom} max={layerMaxZoom} step={1}
          description={tip("minzoom")}
          hint={layer.source ? `Source available from z${srcMinZoom}` : undefined}
        />
        <NumberField
          label="Max Zoom"
          value={layer.maxzoom}
          onChange={(v) => updateLayerCommon(layer.id, "maxzoom", Math.max(layerMinZoom, Math.min(v, 24)))}
          min={layerMinZoom} max={24} step={1}
          hint={layerMinZoom > 0 ? `Must be ≥ min zoom (${layerMinZoom})` : undefined}
        />
      </PropertyGroup>

      {/* ── BACKGROUND ── */}
      {layerType === "background" && (
        <>
          <PropertyGroup title="Color & Opacity" defaultOpen hasValues={hasAny(paint, "background-color", "background-opacity")}>
            <ColorField label="Color" value={colorVal(p("background-color"))} onChange={(v) => sp("background-color", v)} />
            <NumberField label="Opacity" value={p("background-opacity") as number} onChange={(v) => sp("background-opacity", v)} min={0} max={1} step={0.05} />
          </PropertyGroup>
          <PropertyGroup title="Pattern" hasValues={hasAny(paint, "background-pattern")}>
            <SpriteField label="Pattern" value={p("background-pattern") as string} onChange={(v) => sp("background-pattern", v)} />
          </PropertyGroup>
        </>
      )}

      {/* ── FILL ── */}
      {layerType === "fill" && (
        <>
          <PropertyGroup title="Color & Opacity" defaultOpen hasValues={hasAny(paint, "fill-color", "fill-opacity", "fill-outline-color")}>
            <ExprBadge value={p("fill-color")}>
              <ColorField label="Color" value={colorVal(p("fill-color"))} onChange={(v) => sp("fill-color", v)} />
            </ExprBadge>
            <NumberField label="Opacity" value={p("fill-opacity") as number} onChange={(v) => sp("fill-opacity", v)} min={0} max={1} step={0.05} />
            <ExprBadge value={p("fill-outline-color")}>
              <ColorField label="Outline" value={colorVal(p("fill-outline-color"))} onChange={(v) => sp("fill-outline-color", v)} />
            </ExprBadge>
            <BooleanField label="Antialias" value={(p("fill-antialias") as boolean) ?? true} onChange={(v) => sp("fill-antialias", v)} />
          </PropertyGroup>
          <PropertyGroup title="Pattern" hasValues={hasAny(paint, "fill-pattern")}>
            <SpriteField label="Pattern" value={p("fill-pattern") as string} onChange={(v) => sp("fill-pattern", v)} />
          </PropertyGroup>
          <PropertyGroup title="Translate" hasValues={hasAny(paint, "fill-translate")}>
            <ArrayField label="Translate" value={p("fill-translate") as number[]} onChange={(v) => sp("fill-translate", v)} showDefaultBadge />
            <SelectField label="Anchor" value={(p("fill-translate-anchor") as string) ?? "map"} onChange={(v) => sp("fill-translate-anchor", v)} options={anchorOpts} />
          </PropertyGroup>
          <PropertyGroup title="Sort" hasValues={hasAny(layout, "fill-sort-key")}>
            <NumberField label="Sort Key" value={l("fill-sort-key") as number} onChange={(v) => sl("fill-sort-key", v)} />
          </PropertyGroup>
        </>
      )}

      {/* ── LINE ── */}
      {layerType === "line" && (
        <>
          <PropertyGroup title="Stroke" defaultOpen hasValues={hasAny(paint, "line-color", "line-width", "line-opacity")}>
            <ExprBadge value={p("line-color")}>
              <ColorField label="Color" value={colorVal(p("line-color"))} onChange={(v) => sp("line-color", v)} />
            </ExprBadge>
            <NumberField label="Width" value={p("line-width") as number} onChange={(v) => sp("line-width", v)} min={0} max={50} step={0.5} />
            <NumberField label="Opacity" value={p("line-opacity") as number} onChange={(v) => sp("line-opacity", v)} min={0} max={1} step={0.05} />
            <NumberField label="Blur" value={p("line-blur") as number} onChange={(v) => sp("line-blur", v)} min={0} max={20} step={0.5} inactiveDefault={0} />
          </PropertyGroup>
          <PropertyGroup title="Cap & Join" hasValues={hasAny(layout, "line-cap", "line-join")}>
            <SelectField label="Cap" value={(l("line-cap") as string) ?? "butt"} onChange={(v) => sl("line-cap", v)} options={[{ value: "butt", label: "Butt" }, { value: "round", label: "Round" }, { value: "square", label: "Square" }]} />
            <SelectField label="Join" value={(l("line-join") as string) ?? "miter"} onChange={(v) => sl("line-join", v)} options={[{ value: "miter", label: "Miter" }, { value: "round", label: "Round" }, { value: "bevel", label: "Bevel" }]} />
            <NumberField label="Miter Limit" value={(l("line-miter-limit") as number) ?? 2} onChange={(v) => sl("line-miter-limit", v)} min={0} max={10} step={0.1} />
          </PropertyGroup>
          <PropertyGroup title="Dash & Pattern" hasValues={hasAny(paint, "line-dasharray", "line-pattern")}>
            <StringField label="Dash Array" value={p("line-dasharray") ? JSON.stringify(p("line-dasharray")) : ""} onChange={(v) => { try { sp("line-dasharray", JSON.parse(v)); } catch { /* invalid */ } }} placeholder='e.g. [4, 2]' description="JSON array of dash/gap lengths" />
            <SpriteField label="Pattern" value={p("line-pattern") as string} onChange={(v) => sp("line-pattern", v)} />
          </PropertyGroup>
          <PropertyGroup title="Gradient" hasValues={hasAny(paint, "line-gradient")}>
            <StringField label="Gradient" value={p("line-gradient") ? JSON.stringify(p("line-gradient")) : ""} onChange={(v) => { try { sp("line-gradient", JSON.parse(v)); } catch { /* invalid */ } }} placeholder='["interpolate", ...]' description="Requires GeoJSON source with lineMetrics: true" />
          </PropertyGroup>
          <PropertyGroup title="Offset & Gap" hasValues={hasAny(paint, "line-offset", "line-gap-width")}>
            <NumberField label="Offset" value={p("line-offset") as number} onChange={(v) => sp("line-offset", v)} min={-50} max={50} step={0.5} inactiveDefault={0} />
            <NumberField label="Gap Width" value={p("line-gap-width") as number} onChange={(v) => sp("line-gap-width", v)} min={0} max={50} step={0.5} inactiveDefault={0} />
          </PropertyGroup>
          <PropertyGroup title="Translate" hasValues={hasAny(paint, "line-translate")}>
            <ArrayField label="Translate" value={p("line-translate") as number[]} onChange={(v) => sp("line-translate", v)} showDefaultBadge />
            <SelectField label="Anchor" value={(p("line-translate-anchor") as string) ?? "map"} onChange={(v) => sp("line-translate-anchor", v)} options={anchorOpts} />
          </PropertyGroup>
          <PropertyGroup title="Sort" hasValues={hasAny(layout, "line-sort-key")}>
            <NumberField label="Sort Key" value={l("line-sort-key") as number} onChange={(v) => sl("line-sort-key", v)} />
          </PropertyGroup>
        </>
      )}

      {/* ── SYMBOL ── */}
      {layerType === "symbol" && (
        <>
          <PropertyGroup title="Text Content" defaultOpen hasValues={hasAny(layout, "text-field", "text-font", "text-size")}>
            <StringField label="Text Field" value={strOrJson(l("text-field"))} onChange={(v) => sl("text-field", tryParseOrString(v))} placeholder='["get", "name"]' />
            <FontField label="Font" value={l("text-font") as string[] | undefined} onChange={(v) => sl("text-font", v)} description={tip("text-font")} />
            <NumberField label="Size" value={numVal(l("text-size"))} onChange={(v) => sl("text-size", v)} min={0} max={100} step={1} />
            <NumberField label="Max Width" value={numVal(l("text-max-width")) ?? 10} onChange={(v) => sl("text-max-width", v)} min={0} max={50} step={0.5} description="In ems" />
            <NumberField label="Line Height" value={numVal(l("text-line-height")) ?? 1.2} onChange={(v) => sl("text-line-height", v)} min={0} max={5} step={0.1} />
            <NumberField label="Letter Spacing" value={numVal(l("text-letter-spacing")) ?? 0} onChange={(v) => sl("text-letter-spacing", v)} min={-0.5} max={1} step={0.01} inactiveDefault={0} />
            <SelectField label="Transform" value={(l("text-transform") as string) ?? "none"} onChange={(v) => sl("text-transform", v)} options={[{ value: "none", label: "None" }, { value: "uppercase", label: "Uppercase" }, { value: "lowercase", label: "Lowercase" }]} />
            <SelectField label="Justify" value={(l("text-justify") as string) ?? "center"} onChange={(v) => sl("text-justify", v)} options={[{ value: "auto", label: "Auto" }, { value: "left", label: "Left" }, { value: "center", label: "Center" }, { value: "right", label: "Right" }]} />
          </PropertyGroup>
          <PropertyGroup title="Text Position" hasValues={hasAny(layout, "text-anchor", "text-offset", "text-radial-offset", "text-rotate")}>
            <SelectField label="Anchor" value={(l("text-anchor") as string) ?? "center"} onChange={(v) => sl("text-anchor", v)} options={anchorPositionOpts} />
            <ArrayField label="Offset" value={l("text-offset") as number[]} onChange={(v) => sl("text-offset", v)} labels={["X", "Y"]} min={-10} max={10} step={0.5} showDefaultBadge />
            <NumberField label="Radial Offset" value={numVal(l("text-radial-offset"))} onChange={(v) => sl("text-radial-offset", v)} min={0} max={10} step={0.1} inactiveDefault={0} />
            <NumberField label="Rotate" value={numVal(l("text-rotate"))} onChange={(v) => sl("text-rotate", v)} min={0} max={360} step={1} />
            <NumberField label="Padding" value={numVal(l("text-padding")) ?? 2} onChange={(v) => sl("text-padding", v)} min={0} max={50} step={1} />
          </PropertyGroup>
          <PropertyGroup title="Text Paint" defaultOpen hasValues={hasAny(paint, "text-color", "text-halo-color", "text-halo-width")}>
            <ColorField label="Color" value={colorVal(p("text-color"))} onChange={(v) => sp("text-color", v)} />
            <NumberField label="Opacity" value={p("text-opacity") as number} onChange={(v) => sp("text-opacity", v)} min={0} max={1} step={0.05} />
            <ColorField label="Halo Color" value={colorVal(p("text-halo-color"))} onChange={(v) => sp("text-halo-color", v)} />
            <NumberField label="Halo Width" value={p("text-halo-width") as number} onChange={(v) => sp("text-halo-width", v)} min={0} max={10} step={0.5} inactiveDefault={0} />
            <NumberField label="Halo Blur" value={p("text-halo-blur") as number} onChange={(v) => sp("text-halo-blur", v)} min={0} max={10} step={0.5} inactiveDefault={0} />
          </PropertyGroup>
          <PropertyGroup title="Icon" hasValues={hasAny(layout, "icon-image", "icon-size")}>
            <SpriteField label="Image" value={strOrJson(l("icon-image"))} onChange={(v) => sl("icon-image", v)} />
            <NumberField label="Size" value={numVal(l("icon-size")) ?? 1} onChange={(v) => sl("icon-size", v)} min={0} max={10} step={0.1} />
            <NumberField label="Rotate" value={numVal(l("icon-rotate"))} onChange={(v) => sl("icon-rotate", v)} min={0} max={360} step={1} />
            <SelectField label="Anchor" value={(l("icon-anchor") as string) ?? "center"} onChange={(v) => sl("icon-anchor", v)} options={anchorPositionOpts} />
            <ArrayField label="Offset" value={l("icon-offset") as number[]} onChange={(v) => sl("icon-offset", v)} labels={["X", "Y"]} min={-20} max={20} step={1} showDefaultBadge />
            <NumberField label="Padding" value={numVal(l("icon-padding")) ?? 2} onChange={(v) => sl("icon-padding", v)} min={0} max={50} step={1} />
          </PropertyGroup>
          <PropertyGroup title="Icon Paint" hasValues={hasAny(paint, "icon-color", "icon-opacity", "icon-halo-color")}>
            <ColorField label="Color" value={colorVal(p("icon-color"))} onChange={(v) => sp("icon-color", v)} />
            <NumberField label="Opacity" value={p("icon-opacity") as number} onChange={(v) => sp("icon-opacity", v)} min={0} max={1} step={0.05} />
            <ColorField label="Halo Color" value={colorVal(p("icon-halo-color"))} onChange={(v) => sp("icon-halo-color", v)} />
            <NumberField label="Halo Width" value={p("icon-halo-width") as number} onChange={(v) => sp("icon-halo-width", v)} min={0} max={10} step={0.5} inactiveDefault={0} />
            <NumberField label="Halo Blur" value={p("icon-halo-blur") as number} onChange={(v) => sp("icon-halo-blur", v)} min={0} max={10} step={0.5} inactiveDefault={0} />
          </PropertyGroup>
          <PropertyGroup title="Placement" hasValues={hasAny(layout, "symbol-placement", "symbol-spacing", "symbol-sort-key")}>
            <SelectField label="Placement" value={(l("symbol-placement") as string) ?? "point"} onChange={(v) => sl("symbol-placement", v)} options={[{ value: "point", label: "Point" }, { value: "line", label: "Line" }, { value: "line-center", label: "Line Center" }]} />
            <NumberField label="Spacing" value={numVal(l("symbol-spacing")) ?? 250} onChange={(v) => sl("symbol-spacing", v)} min={1} max={1000} step={10} />
            <NumberField label="Sort Key" value={numVal(l("symbol-sort-key"))} onChange={(v) => sl("symbol-sort-key", v)} />
            <SelectField label="Z Order" value={(l("symbol-z-order") as string) ?? "auto"} onChange={(v) => sl("symbol-z-order", v)} options={[{ value: "auto", label: "Auto" }, { value: "viewport-y", label: "Viewport Y" }, { value: "source", label: "Source" }]} />
          </PropertyGroup>
          <PropertyGroup title="Collision" hasValues={hasAny(layout, "text-allow-overlap", "icon-allow-overlap", "text-optional", "icon-optional")}>
            <BooleanField label="Text Overlap" value={l("text-allow-overlap") as boolean} onChange={(v) => sl("text-allow-overlap", v)} description="Show text even when colliding" />
            <BooleanField label="Icon Overlap" value={l("icon-allow-overlap") as boolean} onChange={(v) => sl("icon-allow-overlap", v)} description="Show icon even when colliding" />
            <BooleanField label="Text Optional" value={l("text-optional") as boolean} onChange={(v) => sl("text-optional", v)} description="Hide text if icon collides" />
            <BooleanField label="Icon Optional" value={l("icon-optional") as boolean} onChange={(v) => sl("icon-optional", v)} description="Hide icon if text collides" />
            <BooleanField label="Avoid Edges" value={l("symbol-avoid-edges") as boolean} onChange={(v) => sl("symbol-avoid-edges", v)} />
          </PropertyGroup>
          <PropertyGroup title="Alignment" hasValues={hasAny(layout, "text-rotation-alignment", "text-pitch-alignment", "icon-rotation-alignment")}>
            <SelectField label="Text Rotation" value={(l("text-rotation-alignment") as string) ?? "auto"} onChange={(v) => sl("text-rotation-alignment", v)} options={alignOpts} />
            <SelectField label="Text Pitch" value={(l("text-pitch-alignment") as string) ?? "auto"} onChange={(v) => sl("text-pitch-alignment", v)} options={alignOpts} />
            <SelectField label="Icon Rotation" value={(l("icon-rotation-alignment") as string) ?? "auto"} onChange={(v) => sl("icon-rotation-alignment", v)} options={alignOpts} />
            <SelectField label="Icon Pitch" value={(l("icon-pitch-alignment") as string) ?? "auto"} onChange={(v) => sl("icon-pitch-alignment", v)} options={alignOpts} />
            <BooleanField label="Keep Upright" value={(l("text-keep-upright") as boolean) ?? true} onChange={(v) => sl("text-keep-upright", v)} />
          </PropertyGroup>
          <PropertyGroup title="Translate" hasValues={hasAny(paint, "text-translate", "icon-translate")}>
            <ArrayField label="Text Translate" value={p("text-translate") as number[]} onChange={(v) => sp("text-translate", v)} showDefaultBadge />
            <ArrayField label="Icon Translate" value={p("icon-translate") as number[]} onChange={(v) => sp("icon-translate", v)} showDefaultBadge />
          </PropertyGroup>
        </>
      )}

      {/* ── CIRCLE ── */}
      {layerType === "circle" && (
        <>
          <PropertyGroup title="Appearance" defaultOpen hasValues={hasAny(paint, "circle-color", "circle-radius", "circle-opacity")}>
            <ColorField label="Color" value={colorVal(p("circle-color"))} onChange={(v) => sp("circle-color", v)} />
            <NumberField label="Radius" value={p("circle-radius") as number} onChange={(v) => sp("circle-radius", v)} min={0} max={100} step={1} />
            <NumberField label="Opacity" value={p("circle-opacity") as number} onChange={(v) => sp("circle-opacity", v)} min={0} max={1} step={0.05} />
            <NumberField label="Blur" value={p("circle-blur") as number} onChange={(v) => sp("circle-blur", v)} min={0} max={10} step={0.5} inactiveDefault={0} />
          </PropertyGroup>
          <PropertyGroup title="Stroke" hasValues={hasAny(paint, "circle-stroke-color", "circle-stroke-width")}>
            <ColorField label="Color" value={colorVal(p("circle-stroke-color"))} onChange={(v) => sp("circle-stroke-color", v)} />
            <NumberField label="Width" value={p("circle-stroke-width") as number} onChange={(v) => sp("circle-stroke-width", v)} min={0} max={20} step={0.5} inactiveDefault={0} />
            <NumberField label="Opacity" value={p("circle-stroke-opacity") as number} onChange={(v) => sp("circle-stroke-opacity", v)} min={0} max={1} step={0.05} />
          </PropertyGroup>
          <PropertyGroup title="Behavior" hasValues={hasAny(paint, "circle-pitch-scale", "circle-pitch-alignment", "circle-translate")}>
            <SelectField label="Pitch Scale" value={(p("circle-pitch-scale") as string) ?? "map"} onChange={(v) => sp("circle-pitch-scale", v)} options={[{ value: "map", label: "Map" }, { value: "viewport", label: "Viewport" }]} />
            <SelectField label="Pitch Align" value={(p("circle-pitch-alignment") as string) ?? "viewport"} onChange={(v) => sp("circle-pitch-alignment", v)} options={[{ value: "map", label: "Map" }, { value: "viewport", label: "Viewport" }]} />
            <ArrayField label="Translate" value={p("circle-translate") as number[]} onChange={(v) => sp("circle-translate", v)} showDefaultBadge />
            <SelectField label="Translate Anchor" value={(p("circle-translate-anchor") as string) ?? "map"} onChange={(v) => sp("circle-translate-anchor", v)} options={anchorOpts} />
          </PropertyGroup>
          <PropertyGroup title="Sort" hasValues={hasAny(layout, "circle-sort-key")}>
            <NumberField label="Sort Key" value={l("circle-sort-key") as number} onChange={(v) => sl("circle-sort-key", v)} />
          </PropertyGroup>
        </>
      )}

      {/* ── HEATMAP ── */}
      {layerType === "heatmap" && (
        <>
          <PropertyGroup title="Density" defaultOpen hasValues={hasAny(paint, "heatmap-radius", "heatmap-weight", "heatmap-intensity")}>
            <NumberField label="Radius" value={(p("heatmap-radius") as number) ?? 30} onChange={(v) => sp("heatmap-radius", v)} min={1} max={200} step={1} />
            <NumberField label="Weight" value={(p("heatmap-weight") as number) ?? 1} onChange={(v) => sp("heatmap-weight", v)} min={0} max={10} step={0.1} />
            <NumberField label="Intensity" value={(p("heatmap-intensity") as number) ?? 1} onChange={(v) => sp("heatmap-intensity", v)} min={0} max={10} step={0.1} />
            <NumberField label="Opacity" value={(p("heatmap-opacity") as number) ?? 1} onChange={(v) => sp("heatmap-opacity", v)} min={0} max={1} step={0.05} />
          </PropertyGroup>
          <PropertyGroup title="Color Ramp" hasValues={hasAny(paint, "heatmap-color")}>
            <StringField
              label="Color Expression"
              value={p("heatmap-color") ? JSON.stringify(p("heatmap-color")) : ""}
              onChange={(v) => { try { sp("heatmap-color", JSON.parse(v)); } catch { /* invalid */ } }}
              placeholder='["interpolate", ["linear"], ["heatmap-density"], 0, "blue", 1, "red"]'
              description="Interpolation expression mapping density (0–1) to colors"
            />
          </PropertyGroup>
        </>
      )}

      {/* ── FILL-EXTRUSION ── */}
      {layerType === "fill-extrusion" && (
        <>
          <PropertyGroup title="Appearance" defaultOpen hasValues={hasAny(paint, "fill-extrusion-color", "fill-extrusion-opacity")}>
            <ColorField label="Color" value={colorVal(p("fill-extrusion-color"))} onChange={(v) => sp("fill-extrusion-color", v)} />
            <NumberField label="Opacity" value={p("fill-extrusion-opacity") as number} onChange={(v) => sp("fill-extrusion-opacity", v)} min={0} max={1} step={0.05} />
            <BooleanField label="Vertical Gradient" value={(p("fill-extrusion-vertical-gradient") as boolean) ?? true} onChange={(v) => sp("fill-extrusion-vertical-gradient", v)} description="Shading variation on walls" />
          </PropertyGroup>
          <PropertyGroup title="Height" defaultOpen hasValues={hasAny(paint, "fill-extrusion-height", "fill-extrusion-base")}>
            <NumberField label="Height" value={p("fill-extrusion-height") as number} onChange={(v) => { sp("fill-extrusion-height", v); const base = (p("fill-extrusion-base") as number) ?? 0; if (base > v) sp("fill-extrusion-base", v); }} min={0} max={1000} step={1} inactiveDefault={0} />
            <NumberField label="Base" value={p("fill-extrusion-base") as number} onChange={(v) => { const h = (p("fill-extrusion-height") as number) ?? 0; sp("fill-extrusion-base", Math.min(v, h)); }} min={0} max={((p("fill-extrusion-height") as number) ?? 1000)} step={1} hint="Must be ≤ height" inactiveDefault={0} />
          </PropertyGroup>
          <PropertyGroup title="Pattern" hasValues={hasAny(paint, "fill-extrusion-pattern")}>
            <SpriteField label="Pattern" value={p("fill-extrusion-pattern") as string} onChange={(v) => sp("fill-extrusion-pattern", v)} />
          </PropertyGroup>
          <PropertyGroup title="Translate" hasValues={hasAny(paint, "fill-extrusion-translate")}>
            <ArrayField label="Translate" value={p("fill-extrusion-translate") as number[]} onChange={(v) => sp("fill-extrusion-translate", v)} showDefaultBadge />
            <SelectField label="Anchor" value={(p("fill-extrusion-translate-anchor") as string) ?? "map"} onChange={(v) => sp("fill-extrusion-translate-anchor", v)} options={anchorOpts} />
          </PropertyGroup>
        </>
      )}

      {/* ── RASTER ── */}
      {layerType === "raster" && (
        <>
          <PropertyGroup title="Appearance" defaultOpen hasValues={hasAny(paint, "raster-opacity", "raster-saturation", "raster-contrast")}>
            <NumberField label="Opacity" value={p("raster-opacity") as number} onChange={(v) => sp("raster-opacity", v)} min={0} max={1} step={0.05} />
            <NumberField label="Saturation" value={p("raster-saturation") as number} onChange={(v) => sp("raster-saturation", v)} min={-1} max={1} step={0.05} />
            <NumberField label="Contrast" value={p("raster-contrast") as number} onChange={(v) => sp("raster-contrast", v)} min={-1} max={1} step={0.05} />
            <NumberField label="Hue Rotate" value={p("raster-hue-rotate") as number} onChange={(v) => sp("raster-hue-rotate", v)} min={-180} max={180} step={1} description="Degrees" />
          </PropertyGroup>
          <PropertyGroup title="Brightness" hasValues={hasAny(paint, "raster-brightness-min", "raster-brightness-max")}>
            <NumberField label="Min" value={p("raster-brightness-min") as number} onChange={(v) => { const mx = (p("raster-brightness-max") as number) ?? 1; sp("raster-brightness-min", Math.min(v, mx)); }} min={0} max={(p("raster-brightness-max") as number) ?? 1} step={0.05} />
            <NumberField label="Max" value={p("raster-brightness-max") as number} onChange={(v) => { const mn = (p("raster-brightness-min") as number) ?? 0; sp("raster-brightness-max", Math.max(v, mn)); }} min={(p("raster-brightness-min") as number) ?? 0} max={1} step={0.05} />
          </PropertyGroup>
          <PropertyGroup title="Rendering" hasValues={hasAny(paint, "raster-resampling", "raster-fade-duration")}>
            <SelectField label="Resampling" value={(p("raster-resampling") as string) ?? "linear"} onChange={(v) => sp("raster-resampling", v)} options={[{ value: "linear", label: "Linear (smooth)" }, { value: "nearest", label: "Nearest (pixelated)" }]} />
            <NumberField label="Fade (ms)" value={(p("raster-fade-duration") as number) ?? 300} onChange={(v) => sp("raster-fade-duration", v)} min={0} max={2000} step={50} />
          </PropertyGroup>
        </>
      )}

      {/* ── HILLSHADE ── */}
      {layerType === "hillshade" && (
        <>
          <PropertyGroup title="Illumination" defaultOpen hasValues={hasAny(paint, "hillshade-illumination-direction", "hillshade-illumination-altitude")}>
            <NumberField label="Direction" value={(p("hillshade-illumination-direction") as number) ?? 335} onChange={(v) => sp("hillshade-illumination-direction", v)} min={0} max={359} step={1} description="Degrees (0 = top)" />
            <NumberField label="Altitude" value={(p("hillshade-illumination-altitude") as number) ?? 45} onChange={(v) => sp("hillshade-illumination-altitude", v)} min={0} max={90} step={1} description="Angle above horizon" />
            <SelectField label="Anchor" value={(p("hillshade-illumination-anchor") as string) ?? "viewport"} onChange={(v) => sp("hillshade-illumination-anchor", v)} options={[{ value: "map", label: "Map" }, { value: "viewport", label: "Viewport" }]} />
          </PropertyGroup>
          <PropertyGroup title="Appearance" hasValues={hasAny(paint, "hillshade-exaggeration", "hillshade-shadow-color", "hillshade-highlight-color", "hillshade-accent-color")}>
            <NumberField label="Exaggeration" value={(p("hillshade-exaggeration") as number) ?? 0.43} onChange={(v) => sp("hillshade-exaggeration", v)} min={0} max={1} step={0.05} />
            <ColorField label="Shadow" value={colorVal(p("hillshade-shadow-color")) || "#000000"} onChange={(v) => sp("hillshade-shadow-color", v)} />
            <ColorField label="Highlight" value={colorVal(p("hillshade-highlight-color")) || "#ffffff"} onChange={(v) => sp("hillshade-highlight-color", v)} />
            <ColorField label="Accent" value={colorVal(p("hillshade-accent-color")) || "#000000"} onChange={(v) => sp("hillshade-accent-color", v)} />
          </PropertyGroup>
        </>
      )}
    </div>
  );
}

// ── Shared option lists ──

const anchorOpts = [
  { value: "map", label: "Map" },
  { value: "viewport", label: "Viewport" },
];

const anchorPositionOpts = [
  { value: "center", label: "Center" },
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
];

const alignOpts = [
  { value: "auto", label: "Auto" },
  { value: "map", label: "Map" },
  { value: "viewport", label: "Viewport" },
];

// ── Helpers ──

function colorVal(value: unknown): string {
  if (typeof value === "string") return value;
  return "#000000";
}

function numVal(value: unknown): number | undefined {
  if (typeof value === "number") return value;
  return undefined;
}

function strOrJson(value: unknown): string {
  if (typeof value === "string") return value;
  if (value !== undefined && value !== null) return JSON.stringify(value);
  return "";
}

function tryParseOrString(v: string): unknown {
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
}

/** Shows an "fx" badge when value is an expression */
function ExprBadge({ value, children }: { value: unknown; children: React.ReactNode }) {
  const isExpr = Array.isArray(value) && value.length > 0 && typeof value[0] === "string";
  if (!isExpr) return <>{children}</>;

  return (
    <div className="relative">
      {children}
      <span
        className="absolute -top-1 -right-1 z-10 rounded-radius-full bg-warning px-1 text-[8px] font-bold text-white"
        title={`Expression: ${JSON.stringify(value).slice(0, 100)}...`}
      >
        fx
      </span>
    </div>
  );
}
